import { ServerResponse } from 'http';
import Router from 'next/router';
import * as Sentry from '@sentry/nextjs';

import { FeatureNames } from '@/core/interfaces/store';
import {
  AutoCompleteAddress,
  CustomFieldsForm,
  CustomFieldWithValue,
  extendPrefixTypes,
  Nullable,
  prefixTypes,
  QueryParams,
  TakeActionErrorsFields,
  TakeActionFormState,
} from '@/core/interfaces/common';

import { fieldTypes } from '@/utils/constants';

export const isBrowser = !!(typeof window !== 'undefined' && window.document && window.document.createElement);

export const redirect = (res: ServerResponse | undefined, route: string) => {
  if (!isBrowser && res) {
    res.writeHead(307, {
      Location: route,
    });
    res.end();
  } else {
    Router.replace(route);
  }
};

export const getActionPrefix = (featureName: FeatureNames) => featureName.toUpperCase();

export const formatPhoneNumber = (phoneNumberString: string): Nullable<string> => {
  if (phoneNumberString) {
    const match = phoneNumberString.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return phoneNumberString;
  }

  return null;
};

export const replaceStringLineBreaksWithHtml = (value: string) => {
  if (!value?.replace) {
    return '';
  }

  return value.replace(/(?:\r\n|\r|\n)/g, '<br />');
};

export const handleNumberFormat = (value: string) => {
  const digitsPlus = value.replace(/[^+0-9]/g, '');

  const leadingDigitRegex = /^\+1/;
  const hasLeadingDigit = digitsPlus.match(leadingDigitRegex);
  const withoutLeadingDigit = digitsPlus.replace(leadingDigitRegex, '');

  if (withoutLeadingDigit.length < 10) {
    return digitsPlus;
  }

  return formatPhoneNumber(hasLeadingDigit ? withoutLeadingDigit : digitsPlus);
};

export const hexToRgb = (hex: string, alpha: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return `rgb(${r}, ${g}, ${b})`;
};

export const getFormattedAddress = (option: string | AutoCompleteAddress, isForInput = false) => {
  if (typeof option === 'string') {
    return option;
  }

  let whiteSpace = '';
  let secondary = '';

  if (option.secondary) {
    secondary = option.secondary;

    if (option.entries > 1) {
      secondary += ` (${option.entries} entries)`;
    }
    whiteSpace = ' ';
  }

  if (isForInput) {
    return `${option.streetLine}${whiteSpace}${secondary}`;
  }

  return `${option.streetLine}${whiteSpace}${secondary} ${option.city}, ${option.state} ${option.zipcode}`;
};

export const findLongestCommonSubstring = (input: string, wholeString: string) => {
  for (let i = input.length; i > 0; i -= 1) {
    const reverseInput = Array.from(input.substr(0, i))
      .reverse()
      .join('');
    const reverseWholeString = Array.from(wholeString)
      .reverse()
      .join('');

    const reverseIndex = reverseWholeString.toLowerCase().indexOf(reverseInput.toLowerCase());
    const splitIndex = reverseIndex > 0 ? reverseWholeString.length - reverseIndex : -1;
    const commonPart = wholeString.substr(0, splitIndex);
    const restOfTheString = wholeString.substr(splitIndex);

    if (splitIndex >= 0) {
      return [
        commonPart,
        restOfTheString,
      ];
    }
  }

  return [
    '',
    wholeString,
  ];
};

export const scrollToTop = () => window?.scrollTo({
  behavior: 'smooth',
  top: 0,
});

export const hasAnyError = (error: TakeActionErrorsFields) => Object.entries(error).find(([
  key,
  value,
]) => {
  if (key === 'customFields') {
    return value?.find((customField: { value: [string] }) => {
      if (customField?.value && Array.isArray(customField.value)) {
        if (typeof customField?.value[0] === 'string') {
          return !!value[0];
        }
      }

      return false;
    });
  }

  if (key === 'messages') {
    return value?.some((message: {
      message: [string];
      subject: [string];
    }) => {
      if (message?.message && Array.isArray(message.message)) {
        if (typeof message?.message[0] === 'string') {
          return !!value[0];
        }
      }

      if (message?.subject && Array.isArray(message.subject)) {
        if (typeof message?.subject[0] === 'string') {
          return !!value[0];
        }
      }

      return false;
    });
  }

  if (typeof value === 'string') {
    return !!value;
  }

  if (Array.isArray(value)) {
    if (typeof value[0] === 'string') {
      return !!value[0];
    }
  }

  return false;
});

export const transformPrefix = (prefix: extendPrefixTypes) => {
  if (!prefix) {
    return '';
  }

  if (prefix.endsWith('.')) {
    return prefix as prefixTypes;
  }

  return `${prefix}.` as prefixTypes;
};

export const handleCreateCustomFieldsParams = (
  customFields: Array<CustomFieldWithValue>
): Array<CustomFieldsForm> => customFields.map(({
  fieldType,
  uuid: customFieldUuid,
  value,
  values,
}) => ({
  uuid: customFieldUuid,
  ...(fieldType === fieldTypes.multipleChoiceField ?
    { values: values?.map(({ value: option }) => option) } :
    { value }),
}));

export const isValidUrl = (urlString: string | undefined) => {
  if (!urlString) {
    return false;
  }

  const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]+)?(\/[^\s]*)?$/;
  const isValid = urlRegex.test(urlString);

  if (!isValid) {
    Sentry.captureException(new Error('Url from REACT_APP_ACTION_CENTER_ROOT is not a valid url'));
  }

  return isValid;
};

/*
 Populates the form based on data provided in query parameters.
 This allows a customer to populate forms from their own email
 system.
 */
export const updateFormStateFromQueryParams = (
  queryParams: QueryParams,
  formState: TakeActionFormState
): TakeActionFormState | null => {
  const updatedFormState = { ...formState };
  let hasUpdates = false;

  const formFields = [
    'mus_fname',
    'mus_lname',
    'mus_email',
    'mus_phone',
    'mus_street',
    'mus_zip',
    'mus_prefix',
  ] as const;

  formFields.forEach(field => {
    const value = queryParams[field];

    if (value && typeof value === 'string') {
      switch (field) {
        case 'mus_fname':
          updatedFormState.firstName = value;
          break;
        case 'mus_lname':
          updatedFormState.lastName = value;
          break;
        case 'mus_email':
          updatedFormState.email = value;
          break;
        case 'mus_phone':
          updatedFormState.phoneNumber = handleNumberFormat(value) || '';
          break;
        case 'mus_street':
          updatedFormState.streetAddress = value;
          break;
        case 'mus_zip':
          updatedFormState.zipCode = value;
          break;
        case 'mus_prefix': {
          const validPrefixes = [
            'Ms',
            'Mr',
            'Mrs',
            'Dr',
            'Miss',
            '',
          ] as const;

          const normalizedValue = value
            .toLowerCase()
            .replace(/\.$/, '')
            .replace(/^\w/, c => c.toUpperCase());

          if (normalizedValue.toLowerCase() === 'miss') {
            updatedFormState.prefix = 'Miss' as prefixTypes;
          } else if (validPrefixes.includes(normalizedValue as typeof validPrefixes[number])) {
            updatedFormState.prefix = `${normalizedValue}.` as prefixTypes;
          }
          break;
        }
        default:
          break;
      }
      hasUpdates = true;
    }
  });

  return hasUpdates ? updatedFormState : null;
};
